import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, styled, Typography } from "@material-ui/core";
import { IconAvtar } from "../../contactlist/src/assets";

interface PollData {
  id?: number;
  question?: string;
  comment_id?: number;
  post_id?: number;
  created_at?: string;
  user_voted_option_id?: number | null;
  total_count?: number;
}

interface OptionData {
  id: number;
  post_poll_id: number;
  name: string;
  vote_count: number;
  created_at: string;
  updated_at: string;
}

interface PollProps {
  poll: PollData;
  poll_options: OptionData[];
  handleVote: (optionId: number) => void;
  accountProfile: string | undefined;
  handleDeleteVote: (optionId: number) => void;
  hideLabel?: boolean;
}

const styles: { [key: string]: any } = {
  pollWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "0",
    width: "100%",
  },
  question: {
    marginBottom: "16px",
    fontSize: "16px",
    fontWeight: 600,
    color: "#000",
    lineHeight: "1.4",
  },
  optionBox: {
    display: "flex",
    alignItems: "center",
    margin: "2px 0",
    position: "relative",
    minHeight: "20px",
    height: "20px",
    cursor: "pointer",
    backgroundColor: "#F8FAFC",
    borderRadius: "4px",
    padding: "0 8px",
    border: "none",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#F1F5F9",
    },
  },
  filledBox: {
    position: "absolute",
    backgroundColor: "#64748B",
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.8s ease-in-out",
    left: 0,
    top: 0,
  },
  optionText: {
    position: "relative",
    zIndex: 2,
    paddingRight: "50px",
    color: "#000",
    whiteSpace: "normal",
    wordBreak: "break-word",
    flex: 1,
    fontSize: "15px",
    fontWeight: 500,
  },
  percentageText: {
    position: "absolute",
    right: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 3,
    color: "#FFFFFF",
    fontSize: "14px",
    fontWeight: 600,
  },
  voteCount: {
    fontSize: "13px",
    color: "#8e8e8e",
    marginTop: "12px",
  },
  fallbackAvatar: {},
  avatar: {
    width: "40px",
    height: "40px",
    backgroundColor: "#FFF",
    border: "2px solid #e0e0e0",
    marginBottom: "0",
    "@media (max-width: 950px)": {
      width: "32px",
      height: "32px",
      "& img": {
        height: "32px",
        width: "32px",
      },
    },
  },
  deleteVote: {
    fontSize: "13px",
    color: "#8e8e8e",
    cursor: "pointer",
    marginTop: "12px",
    textDecoration: "underline",
    "&:hover": {
      color: "#000",
    },
  },
  voteCountWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "8px",
  },
  enterVote: {
    fontSize: "13px",
    color: "#8e8e8e",
    fontWeight: 500,
    fontFamily: "Inter, sans-serif",
    cursor: "pointer",
    "&:hover": {
      color: "#000",
    },
  }
};

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: "40px",
  height: "40px",
  backgroundColor: "#FFF",
  border: "2px solid #e0e0e0",
  marginBottom: "0",
  flexShrink: 0,
  "& img": {
    width: "40px",
    height: "40px",
    objectFit: "cover",
  },
  [theme.breakpoints.down(950)]: {
    width: "32px",
    height: "32px",
    "& img": {
      height: "32px",
      width: "32px",
    },
  },
}));

const PollContainer = styled(Box)(({ theme }) => ({
  color: "#000",
  padding: "0",
  borderRadius: "0",
  minWidth: "300px",
  fontFamily: "Inter",
  // flex: 1,
  [theme.breakpoints.down(650)]: {
    minWidth: "250px",
  },
}));

const Poll = ({
  hideLabel = false,
  poll,
  poll_options,
  accountProfile,
  handleVote,
  handleDeleteVote,
}: PollProps) => {
  const [totalVotes, setTotalVotes] = useState<number>(poll?.total_count || 0);
  const [options, setOptions] = useState<OptionData[]>(poll_options);
  const [userVotedOption, setUserVotedOption] = useState<
    number | null | undefined
  >(poll?.user_voted_option_id);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [maxHeight, setMaxHeight] = useState<number>(0);

  useEffect(() => {
    const heights = optionRefs.current.map((el) => (el ? el.offsetHeight : 0));
    const max = Math.max(0, ...heights);
    setMaxHeight(max);
  }, [options, userVotedOption, totalVotes]);

  const calculatePercentage = (count: number): number => {
    return totalVotes > 0
      ? parseFloat(((count / totalVotes) * 100).toFixed(0))
      : 0;
  };

  const handlePollVote = (optionId: number) => {
    if (userVotedOption !== null) return;
    handleVote(optionId);
    setOptions((prevOptions) =>
      prevOptions.map((option) =>
        option.id === optionId
          ? { ...option, vote_count: option.vote_count + 1 }
          : option
      )
    );
    setTotalVotes(totalVotes + 1);
    setUserVotedOption(optionId);
  };

  const deleteVote = () => {
    if (!userVotedOption) return;
    handleDeleteVote(userVotedOption);
    setOptions((prevOptions) =>
      prevOptions.map((option) =>
        option.id === userVotedOption
          ? { ...option, vote_count: option.vote_count - 1 }
          : option
      )
    );
    setUserVotedOption(null);
    setTotalVotes(totalVotes - 1);
  };

	return (
		<Box style={{...styles.pollWrapper, flexDirection: 'column', alignItems: 'center', gap: '12px'}} data-test-id="poll-wrapper">
			<PollContainer style={{ width: '100%', display: 'flex', flexDirection: 'column', }}>
				{/* Question Text */}
        <Box
    style={{
      width: "100%",
      display: "flex",
      justifyContent: "center",
    }}
  >
    {/* QUESTION BOX (Instagram style) */}
    {!hideLabel && <Box
      style={{
        maxHeight: "70px",
        overflowY: "auto",
        width: window.innerWidth < 600 ? "82%" : "90%", 
        backgroundColor: "#ffffff",
        padding: "12px 16px",
        borderRadius: "16px",
        boxShadow: "0 3px 8px rgba(0,0,0,0.12)",
      }}
    >
      <Typography
        variant="subtitle1"
        style={{
          color: "#000",
          fontSize: "18px",
          fontWeight: 600,
          textAlign: "center",
          lineHeight: "22px",
        }}
      >
        {poll?.question}
      </Typography>
    </Box>}
  </Box>

				{/* Poll Options as Buttons */}
				{options.map((option, index) => {
					const isVoted = userVotedOption !== null;
					const percentage = totalVotes > 0 ? calculatePercentage(option.vote_count) : 0;
					
					// Find the option with the highest vote count (leading option)
					const maxVotes = Math.max(...options.map(opt => opt.vote_count));
					const isLeading = option.vote_count === maxVotes && totalVotes > 0;
					
					return (
						<div
							key={option.id}
							onClick={() => !isVoted && handlePollVote(option.id)}
							data-test-id={`poll-profile-wrapper-${index+1}`}
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								position: 'relative',
								width: '100%',
								maxWidth: '200px',
								height: '35px',
								minHeight: '35px',
								cursor: isVoted ? 'default' : 'pointer',
								backgroundColor: '#FFFFFF',
								border: '1px solid #E0E0E0',
								borderRadius: '8px',
								padding: '0 12px',
								margin: '4px 0',
								transition: 'all 0.2s ease',
								overflow: 'hidden'
							}}
							ref={(el: HTMLDivElement | null) => optionRefs.current[index] = el}
						>
							{/* Progress bar - yellow/gold for leading option, light grey for others */}
							{isVoted && totalVotes > 0 && (
								<Box
									style={{
										position: 'absolute',
										backgroundColor: isLeading ? '#FFD700' : '#FFD700',
										height: '100%',
										width: `${percentage}%`,
										borderRadius: '8px',
										transition: 'width 0.8s ease-in-out',
										left: 0,
										top: 0,
										zIndex: 1
									}}
								/>
							)}
							
							{/* Option text */}
							<Typography variant="body2" style={{
								position: 'relative',
								zIndex: 2,
								color: '#000000',
								fontSize: '14px',
								fontWeight: 500,
								textAlign: 'left',
								flex: 1
							}}>
								{option.name}
							</Typography>
							
							{/* Percentage - always show when voted, on the right */}
							{isVoted && totalVotes > 0 && (
								<Typography variant="body2" style={{
									position: 'relative',
									zIndex: 3,
									color: '#000000',
									fontSize: '14px',
									fontWeight: 600,
									marginLeft: '8px',
									minWidth: '40px',
									textAlign: 'right'
								}}>
									{percentage}%
								</Typography>
							)}
						</div>
					);
				})}
			</PollContainer>
		</Box>
	);
};

export default Poll;
